import { Type } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsEnum,
  IsInt,
  IsISO8601,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  IsUUID,
  MaxLength,
  Min,
  ValidateNested,
} from 'class-validator';
import { IngredientDto, SubRecipeRefDto } from './create-recipe.dto';
import { DefrostedReason } from './defrosted-reason.enum';
import { DietaryTag } from './dietary-tag.enum';

/** Nota libera che accompagna il motivo del decongelamento (letta dal cliente sul menù). */
export const MAX_DEFROSTED_NOTE_LENGTH = 300;

export class UpdateRecipeDto {
  /**
   * Optimistic lock: the `updatedAt` the client last read. When present, the
   * server rejects the write with a conflict if the recipe moved on since —
   * two cooks editing the same recipe must not silently clobber each other.
   * Optional so callers that predate this contract (gpt/, WhatsApp agent)
   * keep working unguarded until they adopt it.
   */
  @IsOptional()
  @IsISO8601()
  expectedUpdatedAt?: string;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  name?: string;

  /**
   * Categoria di organizzazione nella sezione ricette. Tri-state come
   * `defrostedNote`: omesso = invariato, uuid = imposta (il backend rifiuta
   * l'id di un altro tenant), `null` = rimuovi dalla categoria.
   */
  @IsOptional()
  // Same persisted-ID boundary as IngredientDto.ingredientId.
  @IsUUID('loose')
  categoryId?: string | null;

  @IsOptional()
  @IsString()
  description?: string | null;

  @IsOptional()
  @IsString()
  notes?: string | null;

  @IsOptional()
  @IsInt()
  @Min(1)
  servings?: number | null;

  @IsOptional()
  @IsInt()
  @Min(1)
  shelfLifeDays?: number | null;

  /** Numero positivo per impostare il peso finito; `null` lo azzera. */
  @IsOptional()
  @IsNumber()
  @IsPositive()
  finishedWeightG?: number | null;

  @IsOptional()
  @IsBoolean()
  servedDefrosted?: boolean;

  /**
   * Motivo e nota vivono solo insieme a `servedDefrosted = true`. La coerenza NON
   * è imposta qui: una PATCH può spegnere il flag senza toccare questi campi, e la
   * validazione non vede lo stato persistito. È `RecipeService.update` ad azzerarli
   * quando il flag risultante è false — unico punto, nessun motivo orfano possibile.
   */
  @IsOptional()
  @IsEnum(DefrostedReason)
  defrostedReason?: DefrostedReason | null;

  @IsOptional()
  @IsString()
  @MaxLength(MAX_DEFROSTED_NOTE_LENGTH)
  defrostedNote?: string | null;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IngredientDto)
  ingredients?: IngredientDto[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => SubRecipeRefDto)
  subRecipes?: SubRecipeRefDto[];

  @IsOptional()
  @IsArray()
  @IsEnum(DietaryTag, { each: true })
  dietaryTags?: DietaryTag[];
}
